// Billing code recommendations + compliance score for a documented note.
// Fetches POST /ai/note-review on demand (nothing persisted). Rendered in the
// floating scribe panel (with "Apply codes to note") and on the scribe
// appointments page (review only; codes are applied from the open note).
import { useState } from 'react';
import {
  ShieldCheck, DollarSign, AlertTriangle, AlertCircle, Info, Clock, Check, Loader2,
  ChevronDown, ChevronRight
} from 'lucide-react';
import { apiService } from '@/services/api';
import { Button } from '../ui/Button';
import { cn } from '@/lib/utils';

const formatMoney = (value) => (value == null ? null : `$${Number(value).toFixed(2)}`);

const scoreClasses = (score) => {
  if (score >= 85) return 'bg-green-100 text-green-700';
  if (score >= 70) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
};

const rateBasisLabel = (review) => {
  const basis = {
    medicare_locality: 'Medicare allowable, locality-adjusted',
    medicare_national: 'Medicare allowable, national (no locality on file)',
    fee_schedule: 'Contracted fee schedule',
    none: 'No rates on file',
  }[review.rate_basis] || review.rate_basis;
  return `${basis}. Evaluated as ${String(review.payer_category_used || '').replace('_', ' ')}.`;
};

/**
 * @param {Object} props
 * @param {string} props.clinicalNoteId
 * @param {(review: Object) => boolean} [props.onApplyCodes] - When given, shows
 *   "Apply codes to note"; return true once the codes were applied.
 */
export function NoteReviewCard({ clinicalNoteId, onApplyCodes }) {
  const [review, setReview] = useState(null);
  const [isReviewing, setIsReviewing] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [codesApplied, setCodesApplied] = useState(false);

  const runReview = async () => {
    if (!clinicalNoteId) return;
    setIsReviewing(true);
    setError(null);
    setCodesApplied(false);
    try {
      const result = await apiService.reviewNote({ clinical_note_id: clinicalNoteId });
      setReview(result);
      setExpanded(true);
    } catch (err) {
      console.error('[NoteReviewCard] Note review failed:', err);
      setError(err.message || 'Review failed');
    } finally {
      setIsReviewing(false);
    }
  };

  const applyCodes = () => {
    if (!onApplyCodes || !review) return;
    const applied = onApplyCodes(review);
    if (applied === false) {
      setError('This template has no CPT entry field to apply codes to');
      return;
    }
    setCodesApplied(true);
  };

  return (
    <div className="space-y-2">
      {!review && (
        <Button onClick={runReview} disabled={isReviewing || !clinicalNoteId} variant="outline" className="w-full">
          {isReviewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
          {isReviewing ? 'Reviewing note...' : 'Review billing & compliance'}
        </Button>
      )}

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-600">{error}</p>
        </div>
      )}

      {review && (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 w-full"
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Billing & Compliance
            <span className={cn('ml-auto px-2 py-0.5 rounded-full text-xs font-semibold', scoreClasses(review.compliance?.score ?? 0))}>
              {review.compliance?.score ?? 0}/100
            </span>
          </button>

          {expanded && (
            <div className="space-y-3 text-left">
              {/* Compliance */}
              <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-gray-500" />
                  <h4 className="text-xs font-medium text-gray-600 uppercase">Compliance</h4>
                </div>
                {review.compliance?.summary && (
                  <p className="text-sm text-gray-700">{review.compliance.summary}</p>
                )}
                {review.compliance?.items?.length > 0 ? (
                  <ul className="space-y-2">
                    {review.compliance.items.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        {item.severity === 'error' ? (
                          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                        ) : item.severity === 'warning' ? (
                          <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                          <p className="text-gray-800">
                            {item.area && <span className="font-medium">{item.area}: </span>}
                            {item.issue}
                          </p>
                          {item.fix && <p className="text-xs text-gray-500 mt-0.5">Fix: {item.fix}</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-green-700">Nothing to correct.</p>
                )}
              </div>

              {/* Time basis */}
              {review.time_basis && (
                <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-600 space-y-1">
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5" />
                    <span>
                      Documented {review.time_basis.documented_minutes ?? '?'} min
                      {review.time_basis.timed_minutes != null && ` · timed ${review.time_basis.timed_minutes} min`}
                      {review.time_basis.recording_minutes != null && ` · recording ${review.time_basis.recording_minutes} min`}
                    </span>
                  </div>
                  {review.time_basis.warning && (
                    <p className="text-amber-700">{review.time_basis.warning}</p>
                  )}
                </div>
              )}

              {/* Recommended codes */}
              <div className="p-3 bg-green-50 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-green-700" />
                  <h4 className="text-xs font-medium text-green-700 uppercase">Recommended codes</h4>
                  {review.estimated_total > 0 && (
                    <span className="ml-auto text-sm font-semibold text-green-800">
                      Est. {formatMoney(review.estimated_total)}
                    </span>
                  )}
                </div>
                {review.recommended_codes?.length > 0 ? (
                  <div className="space-y-2">
                    {review.recommended_codes.map((code, index) => (
                      <div key={index} className="p-2 bg-white rounded border border-green-100">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-mono font-semibold text-gray-900">
                            {code.code}{code.modifiers?.length ? `-${code.modifiers.join('-')}` : ''}
                          </span>
                          <span className="text-xs text-gray-500">{code.timed ? 'Timed' : 'Untimed'}</span>
                          <span className="ml-auto text-sm text-gray-800">
                            {code.line_total != null ? formatMoney(code.line_total) : 'No rate on file'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-700 mt-0.5">{code.description}</p>
                        <p className="text-xs text-gray-500">
                          {code.units} unit{code.units === 1 ? '' : 's'}
                          {code.minutes != null && ` · ${code.minutes} min`}
                          {code.rate != null && code.units > 1 && ` · ${formatMoney(code.rate)}/unit`}
                        </p>
                        {code.rationale && (
                          <p className="text-xs text-gray-600 mt-1">
                            {code.rationale}
                            {code.alternative_to && <span className="text-green-700"> (chosen over {code.alternative_to})</span>}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">No billable services supported by the documentation.</p>
                )}
                {review.missed_opportunities?.length > 0 && (
                  <div className="pt-1">
                    <p className="text-xs font-medium text-gray-600 uppercase mb-1">One detail away</p>
                    <ul className="space-y-1">
                      {review.missed_opportunities.map((item, index) => (
                        <li key={index} className="text-xs text-gray-700">
                          <span className="font-mono font-semibold">{item.code}</span>: {item.what_to_document}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-xs text-gray-500">{rateBasisLabel(review)}</p>
              </div>

              <div className="flex gap-2">
                {onApplyCodes && (
                  <Button
                    onClick={applyCodes}
                    disabled={!review.recommended_codes?.length || codesApplied}
                    className="flex-1"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    {codesApplied ? 'Codes applied to note' : 'Apply codes to note'}
                  </Button>
                )}
                <Button onClick={runReview} disabled={isReviewing} variant="outline" className={onApplyCodes ? '' : 'flex-1'}>
                  {isReviewing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Re-run review'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
